import React, { useState, useEffect } from 'react';
import { Translation, getI18n } from 'react-i18next';
import {
  Affix,
  Card,
  Col,
  Form,
  Input,
  Row,
  Spin,
  Space,
  Button,
  Checkbox,
  Switch,
} from 'antd';
import {
  LoadingOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';

import message from '../../../elements/lib/MessageWrapper';
import FormMetadata from '../../../elements/components/FormMetadata';
import Logger from '../../../../../lib/Logger';
import PartnershipInput from '../../partnerships/containers/PartnershipInputContainer';
import CareGuidePartnerTagInput from '../../careGuidePartnerTags/containers/CareGuidePartnerTagInputContainer';
import AdminPartnerClassInput from '../../adminPartnerClasses/containers/AdminPartnerClassInputContainer';

const RecipientsInputRow = ({ recipient, remove, index }) => (
  <Translation>
    {(t) => (
      <div className='recipient-input-row' style={{ marginBottom: 8 }}>
        <Space
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <Form.Item
            {...recipient}
            rules={[
              { type: 'email', message: t('feedback_validation_email') },
              { required: true, message: t('feedback_validation_required') },
            ]}
            style={{ flex: 1, marginBottom: 0 }}
          >
            <Input autoFocus={index === 0} />
          </Form.Item>

          <MinusCircleOutlined onClick={() => remove(index)} />
        </Space>
      </div>
    )}
  </Translation>
);

const PartnershipTagRecipientForm = ({
  id,
  data,
  errors,
  load,
  isLoading,
  isSubmitting,
  ...props
}) => {
  const [partnershipId, setPartnershipId] = useState(null);
  const [useAdminPartnerClasses, setUseAdminPartnerClasses] = useState(false);
  const [switchEnabledReport, setSwitchEnabledReport] = useState(false);

  const [form] = Form.useForm();

  // form column settings
  const layout = {
    main: {
      labelCol: { span: 3 },
      wrapperCol: { span: 21 },
    },
  };

  // load record data from API
  useEffect(() => {
    load();
  }, [id, load]);

  // update input values when new data is available
  const dataString = JSON.stringify(data);
  useEffect(() => {
    if (id && !isSubmitting) {
      const dataObj = JSON.parse(dataString);
      setPartnershipId(dataObj['partnership'] ?? null);
      const careGuidePartnerTagIds = dataObj[
        'partnership_tags_recipients_care_guide_partner_tags'
      ].map((tag) => tag.id);
      dataObj['partnership_tags_recipients_care_guide_partner_tags'] =
        careGuidePartnerTagIds;

      const adminPartnerClassesIds = dataObj[
        'partnership_tags_recipients_admin_partner_classes'
      ].map((tag) => tag.id);
      dataObj['partnership_tags_recipients_admin_partner_classes'] =
        adminPartnerClassesIds;

      if (adminPartnerClassesIds.length > 0) {
        setUseAdminPartnerClasses(true);
      }
      setSwitchEnabledReport(dataObj['is_enabled'] ?? false);
      form.setFieldsValue(dataObj);
    }
  }, [form, dataString, isSubmitting, id]);

  // handle errors reported by API
  useEffect(() => {
    let firstFieldName = '';
    for (const field in errors) {
      form.setFields([{ name: field, errors: errors[field] }]);
      if (firstFieldName === '') {
        firstFieldName = field;
      }
    }
    form.scrollToField(firstFieldName);
  }, [form, errors]);

  // submit data handler
  const submitData = async (values) => {
    Logger.log('debug', `PartnershipTagRecipientForm.submitData(###)`);

    // API POST/PUT payload
    let payload = {};
    let partnership_tags_recipients_care_guide_partner_tags = [];
    let partnership_tags_recipients_admin_partner_classes = [];

    for (const input of Object.keys(data)) {
      if (input in values) {
        if (['partnership'].includes(input)) {
          payload[input + '_id'] = values[input];
        } else if (
          input === 'partnership_tags_recipients_care_guide_partner_tags'
        ) {
          if (Array.isArray(values[input])) {
            const tagsFilters = values[input].filter((item) => item !== '');
            partnership_tags_recipients_care_guide_partner_tags =
              partnership_tags_recipients_care_guide_partner_tags.concat(
                tagsFilters
              );
          } else {
            if (values[input] !== '') {
              partnership_tags_recipients_care_guide_partner_tags.push(
                values[input]
              );
            }
          }
        } else if (
          input === 'partnership_tags_recipients_admin_partner_classes'
        ) {
          if (Array.isArray(values[input])) {
            const tagsFilters = values[input].filter((item) => item !== '');
            partnership_tags_recipients_admin_partner_classes =
              partnership_tags_recipients_admin_partner_classes.concat(
                tagsFilters
              );
          } else {
            if (values[input] !== '') {
              partnership_tags_recipients_admin_partner_classes.push(
                values[input]
              );
            }
          }
        } else if (input === 'is_enabled') {
          payload[input] = values[input] ? true : false;
        } else if (input === 'recipients') {
          payload[input] = values[input] !== '' ? values[input] : [];
        } else {
          payload[input] = values[input];
        }
      }
    }
    payload['partnership_tags_recipients_admin_partner_classes_list'] =
      partnership_tags_recipients_admin_partner_classes;
    delete payload['partnership_tags_recipients_admin_partner_classes'];
    payload['partnership_tags_recipients_care_guide_partner_tags_list'] =
      partnership_tags_recipients_care_guide_partner_tags;
    delete payload['partnership_tags_recipients_care_guide_partner_tags'];
    // update
    props.update(id, payload, (success) => {
      if (success) {
        load();
        message.success(getI18n().t('feedback_form_success'));
      } else {
        message.error(getI18n().t('feedback_form_error'));
      }
    });
  };

  // form submit handler
  const handleFinish = async (values) => {
    Logger.log('debug', `PartnershipTagRecipientForm.handleFinish(###)`);
    if (!props.isSubmitting) {
      await submitData(values);
    }
  };

  // form error handler
  const handleFinishFailed = ({ values, errorFields, outOfDate }) => {
    Logger.log('debug', `PartnershipTagRecipientForm.handleFinishFailed(###)`);
    message.error(getI18n().t('feedback_form_error'));
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name);
    }
  };

  const handlePartnershipChange = (value) => {
    setPartnershipId(value);
    form.setFieldsValue({
      partnership_tags_recipients_care_guide_partner_tags: [],
    });
  };

  return (
    <Translation>
      {(t) => (
        <>
          <div className='partnership-tag-recipient-form'>
            <Form
              name='parntnership_tag_recipient_form'
              form={form}
              initialValues={data}
              onFinish={handleFinish}
              onFinishFailed={handleFinishFailed}
              validateTrigger='onSubmit'
              {...layout.main}
            >
              <Row gutter={16}>
                <Col xs={24} lg={18}>
                  <Card
                    title={
                      id
                        ? t('partnership_tags_recipients_edit_title')
                        : t('partnership_tags_recipients_add_title')
                    }
                    extra={
                      isLoading ? (
                        <Spin
                          indicator={
                            <LoadingOutlined style={{ fontSize: 20 }} spin />
                          }
                        />
                      ) : null
                    }
                  >
                    <div className='form-group'>
                      <Form.Item
                        name={'is_enabled'}
                        label={t('partnership_tags_recipients_is_enabled')}
                        style={{ marginBottom: '10px', marginTop: '0' }}
                      >
                        <Switch
                          checked={switchEnabledReport}
                          onChange={setSwitchEnabledReport}
                        />
                      </Form.Item>
                    </div>

                    <PartnershipInput
                      form={form}
                      name='partnership'
                      label={t('partnership_tags_recipients_partnership')}
                      disabled={isLoading || isSubmitting}
                      onChange={handlePartnershipChange}
                      rules={[
                        {
                          required: true,
                          message: t('feedback_validation_required'),
                        },
                      ]}
                    />
                    {partnershipId && (
                      <>
                        <CareGuidePartnerTagInput
                          key={partnershipId}
                          name='partnership_tags_recipients_care_guide_partner_tags'
                          disabled={isLoading || isSubmitting}
                          label={t(
                            'partnership_tags_recipients_care_guide_partner_tags'
                          )}
                          filter={{
                            partnership_id: partnershipId,
                          }}
                        />

                        <div className='form-group'>
                          <Form.Item
                            name={null}
                            label={t(
                              'partnership_tags_recipients_use_admin_partner_classes'
                            )}
                            valuePropName='checked'
                          >
                            <Checkbox
                              checked={useAdminPartnerClasses}
                              disabled={isLoading || isSubmitting}
                              onChange={(evt) =>
                                setUseAdminPartnerClasses(evt.target.checked)
                              }
                            ></Checkbox>
                          </Form.Item>
                        </div>
                        {useAdminPartnerClasses && (
                          <AdminPartnerClassInput
                            key={partnershipId}
                            name='partnership_tags_recipients_admin_partner_classes'
                            disabled={isLoading || isSubmitting}
                            label={t(
                              'partnership_tags_recipients_admin_partner_classes'
                            )}
                            filter={{
                              partnership_id: partnershipId,
                            }}
                          />
                        )}
                      </>
                    )}

                    <div className='form-group'>
                      <Form.Item
                        label={t('partnership_tags_recipients_recipients')}
                        labelCol={layout.main.labelCol}
                        wrapperCol={layout.main.wrapperCol}
                      >
                        <Form.List name='recipients'>
                          {(recipients, { add, remove }) => (
                            <>
                              <div className='scroll-recipients'>
                                {recipients.map((recipient, i) => (
                                  <RecipientsInputRow
                                    key={recipient.key}
                                    recipient={recipient}
                                    index={i}
                                    remove={remove}
                                  />
                                ))}
                              </div>

                              <Form.Item style={{ marginBottom: 0 }}>
                                <Button
                                  type='dashed'
                                  onClick={() => add()}
                                  block
                                >
                                  <PlusOutlined />{' '}
                                  {t(
                                    'partnership_tags_recipients_add_recipient'
                                  )}
                                </Button>
                              </Form.Item>
                            </>
                          )}
                        </Form.List>
                      </Form.Item>
                    </div>
                  </Card>
                </Col>

                <Col xs={24} lg={6}>
                  <Affix offsetTop={10}>
                    <Card title={t('form_metadata_header')}>
                      <FormMetadata
                        id={null}
                        showStatus={false}
                        isSubmitting={isSubmitting}
                      />
                    </Card>
                  </Affix>
                </Col>
              </Row>
            </Form>
          </div>
        </>
      )}
    </Translation>
  );
};

export default PartnershipTagRecipientForm;

Logger.log('silly', `PartnershipTagRecipientForm loaded.`);
